import React, { useEffect, useState } from "react";
import { Button, Modal, message, Select, Tag, Space, Dropdown, Menu } from 'antd';
import { Form, InputGroup, Badge } from 'react-bootstrap';
import { SearchOutlined, ShoppingCartOutlined, ClockCircleOutlined, CheckCircleOutlined, TruckOutlined, MedicineBoxOutlined, UserOutlined, PhoneOutlined, MailOutlined, CalendarOutlined, EnvironmentOutlined, FileTextOutlined, MoreOutlined, ReloadOutlined, PlusOutlined, DownloadOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { oneKlicks } from "../../../SDK/oneclick";
import { getuserdata } from "../../../services/apiService";
import Paginationnew from "../../../Uitiles/Paginationnew";

const { Option } = Select;

const NewOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterStatus, setFilterStatus] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [totalPages, setTotalPages] = useState(0);

    // Database table: pharmacy_orders
    // Table structure:
    // - id (primary key)
    // - order_number (unique)
    // - pharmacy_id (foreign key to pharmacy table)
    // - customer_name
    // - customer_phone
    // - customer_email
    // - order_date
    // - status (pending, confirmed, processing, shipped, delivered, cancelled)
    // - total_amount
    // - items (JSON array of order items)
    // - prescription_required (boolean)
    // - prescription_image (file path)
    // - delivery_address
    // - notes
    // - created_at
    // - updated_at

    useEffect(() => {
        loadOrders();
    }, [page, searchTerm, filterStatus]);

    // Refresh orders function
    const refreshOrders = () => {
        loadOrders();
    };

    const loadOrders = async () => {
        try {
            setLoading(true);
            
            // Get pharmacy ID from user data
            const pharmacyId = await getuserdata("id");
            if (!pharmacyId) {
                message.error("Pharmacy ID not found. Please login again.");
                setLoading(false);
                return;
            }

            // Fetch orders from database with pagination
            const tableSchema = "";
            const tableName = "pharmacy_orders";
            const conditions = { pharmacy_id: pharmacyId };
            const orderBy = "id DESC";
            const search = {
                ...searchTerm && {
                    order_number: searchTerm,
                    customer_name: searchTerm,
                    customer_email: searchTerm
                }
            };
            const pagination = {
                page: page,
                limit: limit
            };
            const margedata = [];
            const range = [];

            console.log('Loading orders with params:', { tableName, conditions, orderBy, search, pagination });
            
            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata, range);

            console.log('Orders API response:', response);

            if (!response) {
                throw new Error('No response from server');
            }

            // Handle both response formats: { success: true, data: [...] } or { data: [...] }
            if (response.success === false) {
                console.error('API returned error:', response);
                message.error(response.message || 'Failed to fetch orders');
                setOrders([]);
                setTotalPages(0);
                setLoading(false);
                return;
            }

            if (!response.data) {
                // If no data field, treat as empty result
                setOrders([]);
                setTotalPages(0);
                setLoading(false);
                return;
            }

            // Transform the data to match the expected format
            const transformedOrders = response.data.map(order => ({
                id: order.id,
                orderNumber: order.order_number || order.orderNumber || `ORD-${order.id}`,
                customerName: order.customer_name || order.customerName || 'N/A',
                customerPhone: order.customer_phone || order.customerPhone || 'N/A',
                customerEmail: order.customer_email || order.customerEmail || 'N/A',
                orderDate: order.order_date || order.orderDate || order.created_at || new Date().toISOString(),
                status: order.status || 'pending',
                totalAmount: parseFloat(order.total_amount || order.totalAmount || 0),
                items: Array.isArray(order.items) ? order.items : (typeof order.items === 'string' ? JSON.parse(order.items || '[]') : []),
                prescriptionRequired: Boolean(order.prescription_required || order.prescriptionRequired),
                prescriptionImage: order.prescription_image || order.prescriptionImage || '',
                deliveryAddress: order.delivery_address || order.deliveryAddress || '',
                notes: order.notes || ""
            }));
            
            setOrders(transformedOrders);
            setTotalPages(response.totalPages || 1);
            setLoading(false);
        } catch (error) {
            console.error("Error loading orders:", error);
            message.error("Failed to load orders. Please try again.");
            setOrders([]);
            setTotalPages(0);
            setLoading(false);
        }
    };

    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            // Get pharmacy ID from user data
            const pharmacyId = await getuserdata("id");
            if (!pharmacyId) {
                message.error("Pharmacy ID not found. Please login again.");
                return;
            }

            // Update order status in database
            const response = await oneKlicks.update("", "pharmacy_orders", 
                { 
                    status: newStatus,
                    updated_at: new Date().toISOString()
                }, 
                { 
                    id: orderId, 
                    pharmacy_id: pharmacyId 
                }
            );

            if (response && response.success) {
            // Update local state
            setOrders(prevOrders => 
                prevOrders.map(order => 
                    order.id === orderId ? { ...order, status: newStatus } : order
                )
            );
            
                message.success(`Order status updated to ${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)}`);
            } else {
                message.error("Failed to update order status. Please try again.");
            }
        } catch (error) {
            console.error("Error updating order status:", error);
            message.error("Failed to update order status. Please try again.");
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "pending": return "orange";
            case "confirmed": return "blue";
            case "processing": return "purple";
            case "shipped": return "green";
            case "delivered": return "success";
            case "cancelled": return "red";
            default: return "default";
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "pending": return <ClockCircleOutlined />;
            case "confirmed": return <CheckCircleOutlined />;
            case "processing": return <MedicineBoxOutlined />;
            case "shipped": return <TruckOutlined />;
            case "delivered": return <CheckCircleOutlined />;
            case "cancelled": return <ClockCircleOutlined />;
            default: return <ClockCircleOutlined />;
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const getOrderSummaryStats = () => {
        const total = orders.length;
        const pending = orders.filter(o => o.status === "pending").length;
        const confirmed = orders.filter(o => o.status === "confirmed").length;
        const processing = orders.filter(o => o.status === "processing").length;
        const shipped = orders.filter(o => o.status === "shipped").length;
        const delivered = orders.filter(o => o.status === "delivered").length;
        const cancelled = orders.filter(o => o.status === "cancelled").length;

        return { total, pending, confirmed, processing, shipped, delivered, cancelled };
    };

    const getStatusMenu = (order) => {
        const statusOptions = [];
        
        switch (order.status) {
            case "pending":
                statusOptions.push(
                    { key: "confirmed", label: "Confirm Order", icon: <CheckCircleOutlined /> },
                    { key: "cancelled", label: "Cancel Order", icon: <ClockCircleOutlined /> }
                );
                break;
            case "confirmed":
                statusOptions.push(
                    { key: "processing", label: "Start Processing", icon: <MedicineBoxOutlined /> },
                    { key: "cancelled", label: "Cancel Order", icon: <ClockCircleOutlined /> }
                );
                break;
            case "processing":
                statusOptions.push(
                    { key: "shipped", label: "Mark as Shipped", icon: <TruckOutlined /> },
                    { key: "cancelled", label: "Cancel Order", icon: <ClockCircleOutlined /> }
                );
                break;
            case "shipped":
                statusOptions.push(
                    { key: "delivered", label: "Mark as Delivered", icon: <CheckCircleOutlined /> }
                );
                break;
        }

        return {
            items: statusOptions,
            onClick: ({ key }) => updateOrderStatus(order.id, key)
        };
    };

    const filteredOrders = orders.filter(order => {
        const matchesStatus = filterStatus === "all" || order.status === filterStatus;
        return matchesStatus;
    });

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const stats = getOrderSummaryStats();

    const handleViewOrder = (order) => {
        setSelectedOrder(order);
        setShowOrderModal(true);
    };

    return (
        <>
            <div className="container-fluid py-3">
                <div className="card mb-5">
                    <div className="card-body">
                        <div className="align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <h5 className="mb-0 font-weight-bold mr-3" style={{ color: '#fff' }}>Orders Management</h5>
                                <Badge bg="info">Orders</Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                                <InputGroup className="flex-grow-1 me-2 h-40">
                                    <Form.Control
                                        placeholder="Search by order number, customer name, or email..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </InputGroup>
                                <Select
                                    value={filterStatus}
                                    onChange={setFilterStatus}
                                    style={{ width: 150, marginRight: '12px' }}
                                >
                                    <Option value="all">All Orders</Option>
                                    <Option value="pending">Pending</Option>
                                    <Option value="confirmed">Confirmed</Option>
                                    <Option value="processing">Processing</Option>
                                    <Option value="shipped">Shipped</Option>
                                    <Option value="delivered">Delivered</Option>
                                    <Option value="cancelled">Cancelled</Option>
                                </Select>
                                <Button className="btn-primary h-40" onClick={refreshOrders} loading={loading}>
                                    <ReloadOutlined /> Refresh
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Order Summary Stats */}
                <div className="row mb-4">
                    <div className="col-md-2 col-sm-4 col-6 mb-3">
                        <div className="card text-center">
                            <div className="card-body">
                                <ShoppingCartOutlined style={{ color: '#1890ff', fontSize: '24px', marginBottom: '8px' }} />
                                <h6 className="mb-0" style={{ color: '#fff' }}>Total</h6>
                                <h4 className="mb-0" style={{ color: '#1890ff' }}>{stats.total}</h4>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-2 col-sm-4 col-6 mb-3">
                        <div className="card text-center">
                            <div className="card-body">
                                <ClockCircleOutlined style={{ color: '#fa8c16', fontSize: '24px', marginBottom: '8px' }} />
                                <h6 className="mb-0" style={{ color: '#fff' }}>Pending</h6>
                                <h4 className="mb-0" style={{ color: '#fa8c16' }}>{stats.pending}</h4>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-2 col-sm-4 col-6 mb-3">
                        <div className="card text-center">
                            <div className="card-body">
                                <MedicineBoxOutlined style={{ color: '#722ed1', fontSize: '24px', marginBottom: '8px' }} />
                                <h6 className="mb-0" style={{ color: '#fff' }}>Processing</h6>
                                <h4 className="mb-0" style={{ color: '#722ed1' }}>{stats.processing}</h4>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-2 col-sm-4 col-6 mb-3">
                        <div className="card text-center">
                            <div className="card-body">
                                <TruckOutlined style={{ color: '#52c41a', fontSize: '24px', marginBottom: '8px' }} />
                                <h6 className="mb-0" style={{ color: '#fff' }}>Shipped</h6>
                                <h4 className="mb-0" style={{ color: '#52c41a' }}>{stats.shipped}</h4>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-2 col-sm-4 col-6 mb-3">
                        <div className="card text-center">
                            <div className="card-body">
                                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '24px', marginBottom: '8px' }} />
                                <h6 className="mb-0" style={{ color: '#fff' }}>Delivered</h6>
                                <h4 className="mb-0" style={{ color: '#52c41a' }}>{stats.delivered}</h4>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-2 col-sm-4 col-6 mb-3">
                        <div className="card text-center">
                            <div className="card-body">
                                <ClockCircleOutlined style={{ color: '#ff4d4f', fontSize: '24px', marginBottom: '8px' }} />
                                <h6 className="mb-0" style={{ color: '#fff' }}>Cancelled</h6>
                                <h4 className="mb-0" style={{ color: '#ff4d4f' }}>{stats.cancelled}</h4>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <table hover size="sm" className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Order Number</th>
                                        <th>Customer</th>
                                        <th>Phone</th>
                                        <th>Order Date</th>
                                        <th>Total Amount</th>
                                        <th>Status</th>
                                        <th style={{ width: '320px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="8" className="text-center py-4">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : filteredOrders.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="text-center py-5">
                                                <MedicineBoxOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                                                <h5 className="mb-2" style={{ color: 'white' }}>No orders found</h5>
                                                <p className="mb-3" style={{ color: 'white' }}>
                                                    {searchTerm ? 'Try adjusting your search criteria' : 'No orders available'}
                                                </p>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredOrders.map((item) => (
                                            <tr key={item.id}>
                                                <td data-label="ID">{item.id}</td>
                                                <td data-label="Order Number" style={{ fontWeight: '600', color: '#1890ff' }}>
                                                    {item.orderNumber}
                                                </td>
                                                <td data-label="Customer">
                                                    <div>
                                                        <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                                                            {item.customerName}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#666' }}>{item.customerEmail}</div>
                                                    </div>
                                                </td>
                                                <td data-label="Phone">
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <PhoneOutlined style={{ marginRight: '6px', color: '#52c41a' }} />
                                                        {item.customerPhone}
                                                    </div>
                                                </td>
                                                <td data-label="Order Date">{formatDate(item.orderDate)}</td>
                                                <td data-label="Total Amount" style={{ fontWeight: '600', color: '#1890ff' }}>
                                                    ${item.totalAmount.toFixed(2)}
                                                </td>
                                                <td data-label="Status">
                                                    <Tag 
                                                        color={getStatusColor(item.status)} 
                                                        icon={getStatusIcon(item.status)}
                                                        style={{ 
                                                            borderRadius: '6px',
                                                            fontSize: '12px',
                                                            fontWeight: '500',
                                                            padding: '4px 8px'
                                                        }}
                                                    >
                                                        {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                                    </Tag>
                                                </td>
                                                <td data-label="Action">
                                                    <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                                                        <Button href="#" className="dc-update-servicesss fit-content btn-info" onClick={(e) => {
                                                            e.preventDefault();
                                                            handleViewOrder(item);
                                                        }}>
                                                            <EyeOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                        </Button>
                                                        <Button href="#" className="dc-update-servicesss fit-content" onClick={(e) => {
                                                            e.preventDefault();
                                                            setSelectedOrder(item);
                                                        }}>
                                                            <EditOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                        </Button>
                                                        <Dropdown
                                                            menu={getStatusMenu(item)}
                                                            trigger={['click']}
                                                        >
                                                            <Button href="#" className="dc-update-servicesss fit-content btn-warning" onClick={(e) => e.preventDefault()}>
                                                                <MoreOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                            </Button>
                                                        </Dropdown>
                                                        <Button href="#" className="dc-update-servicesss btn-danger" onClick={(e) => {
                                                            e.preventDefault();
                                                            if (window.confirm(`Are you sure you want to cancel order "${item.orderNumber}"?`)) {
                                                                updateOrderStatus(item.id, 'cancelled');
                                                            }
                                                        }}>
                                                            <DeleteOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Paginationnew currentPage={page} totalPages={totalPages} handlePageChange={handlePageChange} />
                    </div>
                </div>

                {/* Order Details Modal */}
                <Modal
                    title="Order Details"
                    open={showOrderModal}
                    onCancel={() => setShowOrderModal(false)}
                    footer={[
                        <Button key="close" onClick={() => setShowOrderModal(false)}>
                            Close
                        </Button>
                    ]}
                    width={800}
                >
                    {selectedOrder && (
                        <div>
                            <div style={{ marginBottom: '20px' }}>
                                <h4>Order #{selectedOrder.orderNumber}</h4>
                                <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', marginTop: '15px' }}>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <p><strong>Customer Name:</strong> {selectedOrder.customerName}</p>
                                            <p><strong>Email:</strong> {selectedOrder.customerEmail}</p>
                                            <p><strong>Phone:</strong> {selectedOrder.customerPhone}</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p><strong>Order Date:</strong> {formatDate(selectedOrder.orderDate)}</p>
                                            <p><strong>Status:</strong> 
                                                <Tag color={getStatusColor(selectedOrder.status)} style={{ marginLeft: '8px' }}>
                                                    {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                                                </Tag>
                                            </p>
                                            <p><strong>Total Amount:</strong> ${selectedOrder.totalAmount.toFixed(2)}</p>
                                        </div>
                                    </div>
                                    {selectedOrder.deliveryAddress && (
                                        <p><strong>Delivery Address:</strong> {selectedOrder.deliveryAddress}</p>
                                    )}
                                    {selectedOrder.notes && (
                                        <p><strong>Notes:</strong> {selectedOrder.notes}</p>
                                    )}
                                </div>
                            </div>
                            <div>
                                <h5>Order Items</h5>
                                <div className="table-responsive">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th>Item</th>
                                                <th>Quantity</th>
                                                <th>Price</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedOrder.items && selectedOrder.items.length > 0 ? (
                                                selectedOrder.items.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item.name || item.product_name || 'N/A'}</td>
                                                        <td>{item.quantity || 1}</td>
                                                        <td>${parseFloat(item.price || 0).toFixed(2)}</td>
                                                        <td>${(parseFloat(item.price || 0) * (item.quantity || 1)).toFixed(2)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="text-center">No items found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal>
            </div>
        </>
    );
};

export default NewOrders;
